import Immutable from 'immutable'
import { TPick, TRemove } from '../../../types/global'
import { ImmutableValue } from '../index'
import { IList } from '../list'

export type TMapExtractor<M> = M extends IMap<infer S> ? S : never

type TMapExtractor1v<T, K extends keyof T> = T[K] extends IMap<infer U> | undefined ? U : never
type TMapExtractor2v<T, K extends keyof T, L extends keyof TMapExtractor1v<T, K>> = TMapExtractor1v<
  TMapExtractor1v<T, K>,
  L
>
type TMapExtractor3v<
  T,
  K extends keyof T,
  L extends keyof TMapExtractor1v<T, K>,
  M extends keyof TMapExtractor2v<T, K, L>
> = TMapExtractor1v<TMapExtractor2v<T, K, L>, M>
type TMapExtractor4v<
  T,
  K extends keyof T,
  L extends keyof TMapExtractor1v<T, K>,
  M extends keyof TMapExtractor2v<T, K, L>,
  N extends keyof TMapExtractor3v<T, K, L, M>
> = TMapExtractor1v<TMapExtractor3v<T, K, L, M>, N>

export interface IMap<T extends Record<string, unknown>> {
  size: number

  clear(): IMap<T>

  every<K extends keyof T>(predicate: (value: T[K], key: K, iter: this) => boolean, context?: any): boolean

  equals(maybeEqual: IMap<T>): boolean

  filter<K extends keyof T>(predicate: (value: T[K], key: K, iter: this) => boolean, context?: any): IMap<T>

  filterNot<K extends keyof T>(predicate: (value: T[K], key: K, iter: this) => boolean, context?: any): IMap<T>

  find<K extends keyof T>(
    predicate: (value: T, key: K, iter: this) => boolean,
    context?: any,
    notSetValue?: T
  ): T[K] | undefined

  findKey<K extends keyof T>(predicate: (value: T, key: K, iter: this) => boolean, context?: any): K | undefined

  first(): T

  get<K extends keyof T>(key: K): T[K]
  get<K extends keyof T, DV extends NonNullable<T[K]>>(key: K, defaultValue: DV): NonNullable<T[K]>
  get<K extends string, DV extends ImmutableValue>(key: K, defaultValue: DV): DV

  getIn<K extends keyof T>(keys: [K], notSetValue: NonNullable<T[K]>): NonNullable<T[K]>
  getIn<K extends keyof T>(keys: [K]): T[K]
  getIn<K extends keyof T, U extends keyof TMapExtractor1v<T, K>>(
    keys: [K, U],
    notSetValue: NonNullable<TMapExtractor1v<T, K>[U]>
  ): NonNullable<TMapExtractor1v<T, K>[U]>
  getIn<K extends keyof T, U extends keyof TMapExtractor1v<T, K>>(keys: [K, U]): TMapExtractor1v<T, K>[U]
  getIn<K extends keyof T, U extends keyof TMapExtractor1v<T, K>, V extends keyof TMapExtractor2v<T, K, U>>(
    keys: [K, U, V],
    notSetValue: NonNullable<TMapExtractor2v<T, K, U>[V]>
  ): NonNullable<TMapExtractor2v<T, K, U>[V]>
  getIn<K extends keyof T, U extends keyof TMapExtractor1v<T, K>, V extends keyof TMapExtractor2v<T, K, U>>(
    keys: [K, U, V]
  ): TMapExtractor2v<T, K, U>[V]
  getIn<
    K extends keyof T,
    U extends keyof TMapExtractor1v<T, K>,
    V extends keyof TMapExtractor2v<T, K, U>,
    W extends keyof TMapExtractor3v<T, K, U, V>
  >(
    keys: [K, U, V, W],
    notSetValue: NonNullable<TMapExtractor3v<T, K, U, V>[W]>
  ): NonNullable<TMapExtractor3v<T, K, U, V>[W]>
  getIn<
    K extends keyof T,
    U extends keyof TMapExtractor1v<T, K>,
    V extends keyof TMapExtractor2v<T, K, U>,
    W extends keyof TMapExtractor3v<T, K, U, V>
  >(
    keys: [K, U, V, W]
  ): TMapExtractor3v<T, K, U, V>[W]
  getIn<
    K extends keyof T,
    U extends keyof TMapExtractor1v<T, K>,
    V extends keyof TMapExtractor2v<T, K, U>,
    W extends keyof TMapExtractor3v<T, K, U, V>,
    X extends keyof TMapExtractor4v<T, K, U, V, W>
  >(
    keys: [K, U, V, W, X],
    notSetValue: NonNullable<TMapExtractor4v<T, K, U, V, W>[X]>
  ): NonNullable<TMapExtractor4v<T, K, U, V, W>[X]>
  getIn<
    K extends keyof T,
    U extends keyof TMapExtractor1v<T, K>,
    V extends keyof TMapExtractor2v<T, K, U>,
    W extends keyof TMapExtractor3v<T, K, U, V>,
    X extends keyof TMapExtractor4v<T, K, U, V, W>
  >(
    keys: [K, U, V, W, X]
  ): TMapExtractor4v<T, K, U, V, W>[X]
  getIn<K extends (string | number)[]>(keyPath: K): ImmutableValue

  groupBy<G extends ImmutableValue>(
    grouper: (value: T, key: number, iter: this) => G
  ): Immutable.Seq.Keyed<G, Immutable.Collection<number, T>>

  has<K extends keyof T>(key: K): boolean
  has<K extends string>(key: K): boolean

  hasIn<K extends keyof T>(keyPath: [K]): boolean
  hasIn<K extends keyof T>(keyPath: [K, ...string[]]): boolean
  hasIn<K extends (string | number)[]>(keyPath: K): boolean

  isEmpty(): boolean

  join(separator: string): string

  valueSeq<K extends keyof T>(): IList<T[K]>

  // FIXME: the generic below is not necessary
  // NOTE: this workaround is needed as of TypeScript 3.4.5.
  // Typing it as `keys(): Iterable<keyof T>` causes the compiler to run out of memory.
  keys<K extends keyof T>(): Iterable<K>

  last(): T

  map<M extends ImmutableValue, K extends keyof T>(
    mapper: (value: T[K], key: K, iter: this) => M
  ): IMap<{ [key in K]: M }>

  merge<M extends Record<string, unknown>>(map: IMap<M>): IMap<T & M>

  mergeDeep<M extends Record<string, unknown>>(map: IMap<M>): IMap<T & M>

  mergeWith<K extends keyof T>(merger: (oldValue: T, newValue: T, key: K) => T): IMap<T>

  remove<K extends keyof T>(key: K): IMap<TPick<T, Exclude<keyof T, K>>>
  delete<K extends keyof T>(key: K): IMap<TPick<T, Exclude<keyof T, K>>>

  reduce<K extends keyof T, R extends ImmutableValue>(
    reducer: (reduction: R, value: T[K], key: K, iter: this) => R,
    initialReduction: R
  ): R

  reduceRight<K extends keyof T, R extends ImmutableValue>(
    reducer: (reduction: R, value: T[K], key: K, iter: this) => R,
    initialReduction: R
  ): R

  set<K extends keyof T, V extends ImmutableValue>(
    key: K,
    value: V
  ): IMap<{ [key in K]: V } & TPick<T, Exclude<keyof T, K>>>
  set<K extends string, V extends ImmutableValue>(key: K, value: V): IMap<{ [key in K]: V } & T>

  setIn<K extends keyof T, U extends ImmutableValue>(
    keyPath: [K],
    value: U
  ): IMap<{ [P in TRemove<keyof T, K>]: T[P] } & { [key in K]: U }>
  setIn<K extends keyof T, U extends ImmutableValue>(keyPath: [K, ...string[]], value: U): IMap<T>
  setIn<K extends string, L extends string, M extends string, N extends string, U extends ImmutableValue>(
    keyPath: [K, L, M, N],
    value: U
  ): IMap<
    T &
      {
        [KK in K]: IMap<
          {
            [KL in L]: IMap<
              {
                [KM in M]: IMap<
                  {
                    [KN in N]: U
                  }
                >
              }
            >
          }
        >
      }
  >
  setIn<K extends string, L extends string, M extends string, U extends ImmutableValue>(
    keyPath: [K, L, M],
    value: U
  ): IMap<
    T &
      {
        [KK in K]: IMap<
          {
            [KL in L]: IMap<
              {
                [KM in M]: U
              }
            >
          }
        >
      }
  >
  setIn<K extends string, L extends string, U extends ImmutableValue>(
    keyPath: [K, L],
    value: U
  ): IMap<
    T &
      {
        [KK in K]: IMap<
          {
            [KL in L]: U
          }
        >
      }
  >
  setIn<K extends string, U extends ImmutableValue>(keyPath: [K], value: U): IMap<T & { [key in K]: U }>

  some<K extends keyof T>(predicate: (value: T[K], key: K, iter: this) => boolean, context?: any): boolean

  sort<K extends keyof T>(comparator?: (valueA: T[K], valueB: T[K]) => number): IMap<T>

  sortBy<K extends keyof T>(
    comparatorValueMapper: (value: T[K], key: K, iter: this) => ImmutableValue,
    comparator?: (valueA: ImmutableValue, valueB: ImmutableValue) => number
  ): this

  toList<K extends keyof T>(): IList<T[K]>

  toJS(): T

  toOrderedMap(): IMap<T>

  update<K extends keyof T, U extends ImmutableValue>(
    key: K,
    updater: (value: T[K]) => U
  ): IMap<{ [key in K]: U } & TPick<T, Exclude<keyof T, K>>>
  update<K extends string, NV extends ImmutableValue, U extends ImmutableValue>(
    key: K,
    notSetValue: NV,
    updater: (value: NV) => U
  ): IMap<T & { [key in K]: U }>

  updateIn<K extends keyof T, U extends ImmutableValue>(
    keyPath: [K],
    updater: (value: T[K]) => U
  ): IMap<{ [P in TRemove<keyof T, K>]: T[P] } & { [key in K]: U }>
  updateIn<K extends number | string, NV extends ImmutableValue, U extends ImmutableValue>(
    keyPath: [K],
    notSetValue: NV,
    updater: (value: NV) => U
  ): IMap<T & { [key in K]: U }>
  updateIn<K extends keyof T, NV extends ImmutableValue, U extends ImmutableValue>(
    keyPath: [K, ...(number | string)[]],
    notSetValue: NV,
    updater: (value: NV) => U
  ): IMap<T>
  updateIn<K extends keyof T, U extends ImmutableValue>(
    keyPath: [K, ...(number | string)[]],
    updater: (value: ImmutableValue) => U
  ): IMap<T>
  updateIn<K extends number | string, NV extends ImmutableValue, U extends ImmutableValue>(
    keyPath: [K],
    notSetValue: NV,
    updater: (value: NV) => U
  ): IMap<T & { [key in K]: U }>
  updateIn<K extends number | string, L extends number | string, NV extends ImmutableValue, U extends ImmutableValue>(
    keyPath: [K, L],
    notSetValue: NV,
    updater: (value: NV) => U
  ): IMap<
    T &
      {
        [KK in K]: IMap<
          {
            [KL in L]: U
          }
        >
      }
  >
  updateIn<
    K extends string,
    L extends number | string,
    M extends number | string,
    NV extends ImmutableValue,
    U extends ImmutableValue
  >(
    keyPath: [K, L, M],
    notSetValue: NV,
    updater: (value: NV) => U
  ): IMap<
    T &
      {
        [KK in K]: IMap<
          {
            [KL in L]: IMap<
              {
                [KM in M]: U
              }
            >
          }
        >
      }
  >
  updateIn<
    K extends number | string,
    L extends number | string,
    M extends number | string,
    N extends number | string,
    NV extends ImmutableValue,
    U extends ImmutableValue
  >(
    keyPath: [K, L, M, N],
    notSetValue: NV,
    updater: (value: NV) => U
  ): IMap<
    T &
      {
        [KK in K]: IMap<
          {
            [KL in L]: IMap<
              {
                [KM in M]: IMap<
                  {
                    [KN in N]: U
                  }
                >
              }
            >
          }
        >
      }
  >
}

export const Map = <S extends Record<string, unknown>>(shape: S): IMap<S> =>
  (Immutable.Map(shape as any) as unknown) as IMap<S>

Map.isMap = <T extends { [key: string]: unknown }>(maybeMap: unknown): maybeMap is IMap<T> => Immutable.isMap(maybeMap)
